import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../lib/api';
import { getTelegramInitData, getTelegramUser, initTelegramApp, isTelegramMiniApp } from '../lib/telegram';

const ADMIN_TELEGRAM_IDS = [
  '1071709539',
  '6881502327',
  ...((import.meta.env as any).VITE_ADMIN_TELEGRAM_IDS || '').split(','),
]
  .map((s: string) => s.trim())
  .filter(Boolean);

export interface UserProfile {
  id: string;
  name: string;
  role: 'customer' | 'admin' | 'superadmin' | 'guest';
  telegram_id?: string;
  is_banned?: number;
  ban_reason?: string | null;
}

export interface AuthContextType {
  user: UserProfile;
  isMiniApp: boolean;
  isGuest: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>(undefined as any);

export const AuthProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const isMiniApp = isTelegramMiniApp();
  const tgUser = getTelegramUser();

  const [user, setUser] = useState<UserProfile>(() => {
    if (tgUser?.id) {
      const tgIdStr = String(tgUser.id);
      const isTgAdmin = ADMIN_TELEGRAM_IDS.includes(tgIdStr);
      const initialRole = isTgAdmin ? 'admin' : 'customer';
      const userId = isTgAdmin ? `admin-tg-${tgIdStr}` : `user-tg-${tgIdStr}`;
      const name =
        [tgUser.first_name, tgUser.last_name].filter(Boolean).join(' ') ||
        tgUser.username ||
        (isTgAdmin ? 'Admin' : 'Customer');

      api.setCredentials(userId, initialRole);

      return {
        id: userId,
        name,
        role: initialRole,
        telegram_id: tgIdStr,
      };
    }

    return {
      id: '',
      name: 'Guest',
      role: 'guest',
    };
  });

  useEffect(() => {
    initTelegramApp();

    // In Telegram Mini App, submit initData to backend for HMAC verification and DB sync
    const initData = getTelegramInitData();
    if (initData) {
      api
        .authTelegramMiniApp(initData)
        .then((res) => {
          if (res?.data?.user) {
            setUser(res.data.user);
            api.setCredentials(res.data.user.id, res.data.user.role);
          }
        })
        .catch(() => {});
    } else if (user.id) {
      api.setCredentials(user.id, user.role);
    }
  }, []);

  const isGuest = !user.id || user.role === 'guest';
  const isAdmin = user.role === 'admin' || user.role === 'superadmin';

  return (
    <AuthContext.Provider value={{ user, isMiniApp, isGuest, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
